import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { TrendingUp, Brain, Cpu, Target } from "lucide-react";

const STATS = [
  { value: 250, suffix: "B+", label: "Creator economy by 2030", sub: "Goldman Sachs · Forecast" },
  { value: 1.4, suffix: "T", label: "Knowledge economy TAM", sub: "Premium info & education" },
  { value: 67, suffix: "%", label: "Of brands shifting to intent", sub: "Premium adjacency demand" },
  { value: 4.5, suffix: "x", label: "Lift on directed-intent ads", sub: "vs. broad reach campaigns" },
];

const PILLARS = [
  {
    icon: TrendingUp,
    title: "Creator economy",
    desc: "From volatile income to durable revenue. F3 stabilizes the creator stack with ownership and stack-wide monetization.",
  },
  {
    icon: Brain,
    title: "Knowledge economy",
    desc: "Premium news, current affairs, and informed analysis as the new content gold standard — beyond entertainment.",
  },
  {
    icon: Cpu,
    title: "AI media infrastructure",
    desc: "An AI substrate that organizes signal, scores integrity, and powers every layer of the operating system.",
  },
  {
    icon: Target,
    title: "Premium advertising",
    desc: "Intent-rich audiences and brand-safe adjacency that translate to genuine lift, not vanity reach.",
  },
];

function Counter({ to, suffix = "", duration = 1.6 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(to * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  const formatted = to >= 100 ? Math.round(val) : val.toFixed(1);
  return (
    <span ref={ref} className="tabular">
      {formatted}
      {suffix}
    </span>
  );
}

export default function Market() {
  return (
    <section
      id="market"
      data-testid="section-market"
      className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden"
    >
      {/* dim editorial bg */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.08]"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1777187785273-5818e7d84997?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          mixBlendMode: "screen",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 md:mb-20">
          <div className="lg:col-span-7">
            <div className="text-[11px] font-mono tracking-[0.3em] text-violet-300 uppercase mb-6">
              Chapter 04 · Market
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-white tracking-tight leading-[1.0]">
              From the attention economy
              <br />
              to the{" "}
              <span className="text-zinc-400">intent economy</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="text-zinc-400 text-base leading-relaxed">
              The next decade of media will not be won on watch-time — it will
              be won on intent, integrity, and informed action. F3 is the
              infrastructure for that future.
            </p>
          </div>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-20">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              data-testid={`market-stat-${i}`}
              className="bg-black p-8 md:p-10"
            >
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-4">
                {String(i + 1).padStart(2, "0")} · Forecast
              </div>
              <div className="font-heading text-5xl md:text-6xl text-white mb-3 leading-none">
                $<Counter to={s.value} suffix={s.suffix} />
              </div>
              <div className="text-sm text-zinc-300 mb-1">{s.label}</div>
              <div className="text-[11px] font-mono text-zinc-600">{s.sub}</div>
            </motion.div>
          ))}
        </div>

        {/* Opportunity pillars */}
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-8">
            Opportunity surfaces
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                  data-testid={`market-pillar-${i}`}
                  className="group border border-white/10 bg-black p-6 hover:border-white/30 hover:-translate-y-0.5 transition-all"
                >
                  <Icon className="w-5 h-5 text-zinc-400 group-hover:text-violet-300 transition-colors mb-5" strokeWidth={1.5} />
                  <div className="font-heading text-xl text-white mb-2">{p.title}</div>
                  <p className="text-sm text-zinc-400 leading-relaxed">{p.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
